// ProductForm.tsx
import React, { useEffect, useState, useRef } from "react";
import { ErrorMessage, Field, useFormik } from "formik";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { Autocomplete, Box, Checkbox, TextField } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

// Components
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import {
  autocompleteDropdownStyles,
  getDisabledInputStyles,
} from "Constants/Style";

// Thunks
import {
  addProductsAndServices,
  fetchProductsAndServicesById,
  updateProductsAndServices,
} from "slices/Masters/productsandservices/thunk";
import { addType, fetchTypesBySubCategory } from "slices/Masters/type/thunk";
import { fetchCategoryByDropDown } from "slices/Masters/category/thunk";
import { fetchSuCategoryBasedOnSelectedCategory } from "slices/Masters/sub-category/thunk";
import { fetchHsnCodeByDropDown } from "slices/Masters/hsn-code/thunk";
import { fetchUOMByDropDown } from "slices/Masters/unit/thunk";
import { fetchMakeByDropDown } from "slices/Masters/make/thunk";
import { addModel, fetchModelByMakeDropDown } from "slices/Masters/model/thunk";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { category } from "Common/data";
import { MecordColors } from "Constants/Color";
// import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import BootstrapTextField from "Common/BootstrapTextField";
import {
  addProduct,
  fetchProductById,
  updateProduct,
} from "slices/Masters/product/thunk";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import { config } from "config/env";
import generateProductName from "../SlugGenerator/generateProductName";
import TextEditor from "components/TextEditor/TextEditor";
import { Upload } from "lucide-react";

interface ProductFormValues {
  category: string;
  sub_category: string;
  type: string[];
  make: string;
  model: string;
  product_name: string;
  product_sku: string;
  unit: string;
  unit_price: string | number;
  hsn: string;
  gst: string;
  description?: string;
  status?: boolean;
  document_urls: [];
}

const commonInputStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
};

interface ProductFormProps {
  isModal?: boolean;
  onSuccess?: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({
  isModal = false,
  onSuccess,
}) => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [dropDownLoading, setDropDownLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const imagePreviewUrlRef = useRef<string | null>(null);

  // Dropdown States
  const [categories, setCategories] = useState<any>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [makes, setMakes] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const [hsnCodes, setHsnCodes] = useState<any[]>([]);
  const [showQuickTypeModal, setShowQuickTypeModal] = useState(false);
  const [showQuickModelModal, setShowQuickModelModal] = useState(false);
  const [quickTypeName, setQuickTypeName] = useState("");
  const [quickModelName, setQuickModelName] = useState("");
  const [isQuickTypeSaving, setIsQuickTypeSaving] = useState(false);
  const [isQuickModelSaving, setIsQuickModelSaving] = useState(false);

  // Refs for tracking changes
  const prevCategoryRef = useRef<string>("");
  const prevMakeRef = useRef<string>("");

  // Single blob URL for new image preview (revoke on clear/unmount)
  useEffect(() => {
    if (image) {
      if (imagePreviewUrlRef.current) URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = URL.createObjectURL(image);
      return () => {
        if (imagePreviewUrlRef.current) {
          URL.revokeObjectURL(imagePreviewUrlRef.current);
          imagePreviewUrlRef.current = null;
        }
      };
    } else {
      if (imagePreviewUrlRef.current) {
        URL.revokeObjectURL(imagePreviewUrlRef.current);
        imagePreviewUrlRef.current = null;
      }
    }
    console.log("image", image);
    console.log("imagePreviewUrlRef", imagePreviewUrlRef.current);
  }, [image]);

  // Validation Schema
  const validationSchema = Yup.object().shape({
    category: Yup.string().required("Category is required"),
    sub_category: Yup.string().required("Sub-Category is required"),
    type: Yup.array().of(Yup.string()).min(1, "Select at least one type"),
    make: Yup.string().required("Make is required"),
    model: Yup.string().required("Model is required"),
    product_name: Yup.string()
      .trim()
      .required("Product Name is required")
      .min(2, "Product Name must be at least 2 characters")
      .max(200, "Product Name must not exceed 100 characters"),
    product_sku: Yup.string()
      .trim()
      .required("SKU Code is required")
      .min(2, "SKU Code must be at least 2 characters"),
    unit: Yup.string().required("Unit is required"),
    unit_price: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? undefined : value
      )
      .typeError("Unit Price must be a number")
      .min(0, "Unit Price cannot be negative")
      .notRequired()
      .test(
        "decimal-places",
        "Unit Price can have at most 2 decimal places",
        (value) => {
          if (!value) return true;
          return /^\d+(\.\d{1,2})?$/.test(value.toString());
        },
      ),
    hsn: Yup.string().required("HSN Code is required"),
    gst: Yup.string()
      .required("GST % is required")
      .test("is-valid-gst", "GST % must be between 0 and 100", (value) => {
        if (!value) return false;
        const num = parseFloat(value);
        return !isNaN(num) && num >= 0 && num <= 100;
      }),
    // status: Yup.boolean(),
  });

  // Initial Values
  const initialValues: ProductFormValues = {
    category: "",
    sub_category: "",
    type: [],
    make: "",
    model: "",
    product_name: "",
    product_sku: "",
    unit: "",
    unit_price: "",
    hsn: "",
    gst: "",
    document_urls: [],
    description: "",
    // status: true,
  };

  // Formik
  const formik = useFormik<ProductFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  // Submit Handler
  async function handleSubmit(values: ProductFormValues) {
    setIsSubmitting(true);
    try {
      const formData = createFormData(values);

      if (isEdit && id) {
        await dispatch(updateProduct({ id, data: formData })).unwrap();
        toast.success("Product updated successfully");
        if (isModal && onSuccess) {
          onSuccess();
        } else {
          navigate("/master/product");
        }
      } else {
        await dispatch(addProduct(formData)).unwrap();
        clearTableState(TableKeys.MASTER.PRODUCT);
        toast.success("Product added successfully");
      }

      if (isModal && onSuccess) {
        onSuccess();
      } else {
        navigate("/master/product");
      }
    } catch (err: any) {
      handleSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  // Create FormData for product create/update (with optional product_image)
  function createFormData(values: ProductFormValues): FormData {
    const formData = new FormData();

    formData.append("product_name", values.product_name.trim());
    formData.append("product_sku", values.product_sku.trim());
    formData.append("category_id", values.category);
    formData.append("sub_category_id", values.sub_category);
    formData.append("make_id", values.make);
    formData.append("model_id", values.model);
    formData.append("unit_id", values.unit);
    if (values.unit_price !== "" && values.unit_price !== null && values.unit_price !== undefined) {
      formData.append("unit_price", String(values.unit_price));
    }
    formData.append("hsn_id", values.hsn || "");

    if (values.type && values.type.length > 0) {
      formData.append("type_ids", JSON.stringify(values.type));
    }

    if (values.description) {
      formData.append("description", values.description);
    }

    if (image) {
      formData.append("product_image", image);
    }

    return formData;
  }

  // Create JSON payload (kept for reference / non-FormData consumers)
  function createJsonPayload(values: ProductFormValues) {
    const payload: any = {
      product_name: values.product_name.trim(),
      product_sku: values.product_sku.trim(),
      category_id: values.category,
      sub_category_id: values.sub_category,
      make_id: values.make,
      model_id: values.model,
      unit_id: values.unit,
      unit_price: values.unit_price,
      hsn_id: values.hsn,
      // documents: values.document_urls,
      // remark: "",
    };

    if (values.type && values.type.length > 0) {
      payload.type_ids = values.type; // can send as array if server accepts it
    }

    // Optional fields
    if (values.description) {
      // For non-FormData consumers, send description (service maps to prod_description)
      payload.description = values.description;
    }

    return payload;
  }

  // Handle Submit Error
  function handleSubmitError(err: any) {
    const duplicateMessage = err?.errors?.item_name?.[0];
    if (duplicateMessage) {
      toast.error("Product with this name already exists");
    } else if (err?.errors) {
      // Handle multiple field errors
      const firstError = Object.values(err.errors)[0];
      toast.error(
        Array.isArray(firstError) ? firstError[0] : "Failed to save product",
      );
    } else {
      toast.error(err?.message || "Failed to save product");
    }
  }

  // Load Initial Dropdowns
  useEffect(() => {
    async function loadDropdowns() {
      setDropDownLoading(true);
      try {
        const [catRes, hsnRes, uomRes, makeRes] = await Promise.all([
          dispatch(fetchCategoryByDropDown()).unwrap(),
          dispatch(fetchHsnCodeByDropDown()).unwrap(),
          dispatch(fetchUOMByDropDown()).unwrap(),
          dispatch(fetchMakeByDropDown()).unwrap(),
        ]);

        setCategories(catRes || []);
        setHsnCodes(hsnRes?.data || []);
        setUnits(uomRes?.data || []);
        setMakes(makeRes?.data || []);
      } catch (error) {
        toast.error("Failed to load dropdown data");
      } finally {
        setDropDownLoading(false);
      }
    }
    loadDropdowns();
  }, []);

  // Load Subcategories when Category changes
  useEffect(() => {
    const currentCategory = formik.values.category;

    // Reset sub_category when category changes
    if (
      prevCategoryRef.current &&
      prevCategoryRef.current !== currentCategory
    ) {
      formik.setFieldValue("sub_category", "");
      formik.setFieldValue("type", []);
    }
    prevCategoryRef.current = currentCategory;

    if (currentCategory) {
      loadSubCategories(currentCategory);
    } else {
      setSubCategories([]);
    }
  }, [formik.values.category]);

  async function loadSubCategories(categoryId: string) {
    try {
      const res = await dispatch(
        fetchSuCategoryBasedOnSelectedCategory(categoryId),
      ).unwrap();
      setSubCategories(res || []);
    } catch (error) {
      setSubCategories([]);
    }
  }

  // Load Types when Sub-category changes
  useEffect(() => {
    if (formik.values.sub_category) {
      loadTypes(formik.values.sub_category);
    } else {
      setTypes([]);
      formik.setFieldValue("type", []);
    }
  }, [formik.values.sub_category]);

  async function loadTypes(subCategoryId: string) {
    try {
      const res = await dispatch(
        fetchTypesBySubCategory(subCategoryId),
      ).unwrap();

      setTypes(res?.data || []);
    } catch (error) {
      setTypes([]);
    }
  }

  // Load Models when Make changes
  useEffect(() => {
    const currentMake = formik.values.make;

    // Reset model when make changes
    if (prevMakeRef.current && prevMakeRef.current !== currentMake) {
      formik.setFieldValue("model", "");
    }
    prevMakeRef.current = currentMake;

    if (currentMake) {
      loadModels(currentMake);
    } else {
      setModels([]);
    }
  }, [formik.values.make]);

  async function loadModels(makeId: string) {
    try {
      const res = await dispatch(fetchModelByMakeDropDown(makeId)).unwrap();
      setModels(res?.data || []);
    } catch (error) {
      setModels([]);
    }
  }
  const getId = (option: any, ...keys: string[]) => {
    for (const key of keys) {
      if (option?.[key]) return option[key];
    }
    return option?.id || "";
  };

  const handleQuickAddType = async () => {
    const typeName = quickTypeName.trim();

    if (!formik.values.category || !formik.values.sub_category) {
      toast.error("Please select category and sub-category first");
      return;
    }

    if (!typeName) {
      toast.error("Please enter type name");
      return;
    }

    try {
      setIsQuickTypeSaving(true);
      const result = await dispatch(
        addType({
          category_id: formik.values.category,
          sub_category_id: formik.values.sub_category,
          type_name: typeName,
        })
      ).unwrap();

      const refreshed = await dispatch(
        fetchTypesBySubCategory(formik.values.sub_category)
      ).unwrap();
      const refreshedTypes = refreshed?.data || [];
      const createdType = result?.data?.data || result?.data || result;
      const fallbackType = refreshedTypes.find(
        (type: any) =>
          type.type_name?.toLowerCase() === typeName.toLowerCase()
      );
      const createdId =
        getId(createdType, "type_id", "id") ||
        getId(fallbackType, "type_id", "id");

      setTypes(refreshedTypes);
      if (createdId && !formik.values.type.includes(createdId)) {
        formik.setFieldValue("type", [...formik.values.type, createdId]);
      }
      setQuickTypeName("");
      setShowQuickTypeModal(false);
      toast.success("Type added successfully");
    } catch (err: any) {
      toast.error(err?.message || err?.data?.message || "Failed to add type");
    } finally {
      setIsQuickTypeSaving(false);
    }
  };

  const handleQuickAddModel = async () => {
    const modelName = quickModelName.trim();

    if (!formik.values.make) {
      toast.error("Please select make first");
      return;
    }

    if (!modelName) {
      toast.error("Please enter model name");
      return;
    }

    try {
      setIsQuickModelSaving(true);
      const result = await dispatch(
        addModel({ make_id: formik.values.make, model_name: modelName })
      ).unwrap();

      const refreshed = await dispatch(
        fetchModelByMakeDropDown(formik.values.make)
      ).unwrap();
      const refreshedModels = refreshed?.data || [];
      const createdModel = result?.data?.data || result?.data || result;
      const fallbackModel = refreshedModels.find(
        (model: any) =>
          model.model_name?.toLowerCase() === modelName.toLowerCase()
      );
      const createdId =
        getId(createdModel, "model_id", "id") ||
        getId(fallbackModel, "model_id", "id");

      setModels(refreshedModels);
      if (createdId) {
        formik.setFieldValue("model", createdId);
      }
      setQuickModelName("");
      setShowQuickModelModal(false);
      toast.success("Model added successfully");
    } catch (err: any) {
      toast.error(err?.message || err?.data?.message || "Failed to add model");
    } finally {
      setIsQuickModelSaving(false);
    }
  };

  useEffect(() => {}, [categories, subCategories, types]);

  // Load Product Data in Edit Mode
  useEffect(() => {
    if (isEdit && id) {
      loadProductData(id);
    }
  }, [isEdit, id]);

  async function loadProductData(productId: string) {
      setIsLoading(true);
    try {
      const response = await dispatch(fetchProductById(productId)).unwrap();
      const data = response?.data ?? response;

      await loadSubCategories(data.category_id);
      await loadTypes(data.sub_category_id);
      await loadModels(data.make_id);

      formik.setValues({
        category: data.category_id || "",
        sub_category: data.sub_category_id || "",
        type: data.product_types.map((data: any) => data?.type?.type_id) || [],
        make: data.make_id || "",
        model: data.model_id || "",
        product_name: data.product_name || "",
        product_sku: data.product_sku || "",
        unit: data.unit_id || "",
        unit_price: data.unit_price ?? "",
        hsn: data.hsn_id || "",
        gst: data.hsn?.gst?.gst_percentage || "",
        document_urls: data?.document_urls || [],
        // status: data.status ?? true,
        description:
          (data as any).prod_description ||
          (data as any).description ||
          "",
      });

      const rawImageUrl = (data as any)?.image_url || (data as any)?.attachment_url || null;
      const imageUrl =
        rawImageUrl && rawImageUrl.startsWith("/")
          ? `${(config.api?.baseUrl || "").replace(/\/$/, "")}${rawImageUrl}`
          : rawImageUrl;
      setExistingImageUrl(imageUrl);
    } catch (error) {
      // toast.error("Failed to fetch product details");
    } finally {
      setIsLoading(false);
    }
  }

  // Generate Product Name on change
  useEffect(() => {
    const selectedMake = makes.find(
      (m) => m.make_id === formik.values.make,
    )?.make_name;

    const selectedCategory = categories.find(
      (c: any) => c.category_id === formik.values.category,
    )?.category_name;

    const selectedSubCategory = subCategories.find(
      (sc) => sc.sub_category_id === formik.values.sub_category,
    )?.sub_category_name;

    const selectedModel = models.find(
      (m) => m.model_id === formik.values.model,
    )?.model_name;

    const selectedTypes = types
      .filter((t) => formik.values.type.includes(t.type_id))
      .map((t) => t.type_name);

    const productName = generateProductName({
      make: selectedMake || "",
      category: selectedCategory || "",
      subCategory: selectedSubCategory || "",
      type: selectedTypes || [],
      model: selectedModel || "",
    });

    if (formik.values.product_name !== productName) {
      formik.setFieldValue("product_name", productName);
    }
  }, [
    formik.values.make,
    formik.values.category,
    formik.values.sub_category,
    formik.values.type,
    formik.values.model,
    makes,
    categories,
    subCategories,
    types,
    models,
  ]);

  const formContent = (
    <>
      {!isModal && (
        <>
          <PageBreadcrumb
            title={isEdit ? "Edit Product" : "Add Product"}
            titleLink="/master/product"
            breadcrumbs={[
              { label: "Product", to: "/master/product" },
              { label: isEdit ? "Edit Product" : "Add Product", to: "" },
            ]}
            backNavigationPath={-1}
          />
          <hr />
        </>
      )}
      {isEdit && isLoading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate onSubmit={formik.handleSubmit}>
          {/* Basic Information */}
          <Row className="mb-3">
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Product Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={formik.values.product_name}
                  // onChange={formik.handleChange}
                  // onBlur={formik.handleBlur}
                  disabled
                  placeholder="Autofill"
                  // isInvalid={
                  //   !!(
                  //     formik.errors.product_name && formik.touched.product_name
                  //   )
                  // }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                {/* <Form.Control.Feedback type="invalid">
                  {formik.errors.product_name}
                </Form.Control.Feedback> */}
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Category <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={categories}
                  loading={dropDownLoading}
                  getOptionLabel={(option: any) => option.category_name || ""}
                  value={
                    categories.find(
                      (c: any) => c.category_id === formik.values.category,
                    ) || null
                  }
                  onChange={(event, newValue: any) => {
                    formik.setFieldValue(
                      "category",
                      newValue?.category_id || "",
                    );
                    // formik.setFieldTouched("category", true);
                  }}
                  onBlur={() => formik.setFieldTouched("category", true)}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt.category_name,
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="category"
                      placeholder="Select Category"
                      variant="outlined"
                      size="small"
                      error={
                        !!(formik.errors.category && formik.touched.category)
                      }
                      helperText={
                        formik.touched.category
                          ? (formik.errors.category as string)
                          : ""
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Sub-Category <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={subCategories}
                  getOptionLabel={(option: any) =>
                    option.sub_category_name || ""
                  }
                  value={
                    subCategories.find(
                      (sc) => sc.sub_category_id === formik.values.sub_category,
                    ) || null
                  }
                  onChange={(event, newValue: any) => {
                    formik.setFieldValue(
                      "sub_category",
                      newValue?.sub_category_id || "",
                    );
                    // formik.setFieldTouched("sub_category", true);
                  }}
                  onBlur={() => formik.setFieldTouched("sub_category", true)}
                  disabled={!formik.values.category}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt.sub_category_name,
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="sub_category"
                      placeholder="Select Sub-Category"
                      variant="outlined"
                      size="small"
                      error={
                        !!(
                          formik.errors.sub_category &&
                          formik.touched.sub_category
                        )
                      }
                      helperText={
                        formik.touched.sub_category
                          ? (formik.errors.sub_category as string)
                          : ""
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(!formik.values.category),
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} md={4}>
              <Form.Group>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="fw-semibold mb-0">
                    Type <span className="text-danger">*</span>
                  </Form.Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="p-0"
                    onClick={() => setShowQuickTypeModal(true)}
                  >
                    + Add Type
                  </Button>
                </div>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={types}
                  getOptionLabel={(option) => option.type_name || ""}
                  value={types.filter((type: any) =>
                    formik.values.type.includes(type.type_id),
                  )}
                  onChange={(event, selectedTypes) => {
                    const selectedIds = selectedTypes.map(
                      (type) => type.type_id,
                    );
                    formik.setFieldValue("type", selectedIds);
                  }}
                  onBlur={() => formik.setFieldTouched("type", true)}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ padding: 0 }}>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.8,
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",

                          // ✅ Hover state
                          "&:hover": {
                            backgroundColor: MecordColors.Hover,
                            color: MecordColors.ActiveText,
                          },

                          // ✅ Selected state
                          ...(selected && {
                            backgroundColor: MecordColors.ActiveBackground,
                            color: MecordColors.ActiveText,
                          }),
                        }}
                      >
                        {/* Option Label */}
                        <span>
                          {typeof option === "string"
                            ? option
                            : option.type_name}
                        </span>

                        {/* Show X only if option is selected */}
                        {selected && (
                          <span
                            style={{
                              cursor: "pointer",
                              marginLeft: "10px",
                              color: "#d32f2f",
                              fontWeight: "bold",
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // prevent normal selection toggling
                              const newValues = formik.values.type.filter(
                                (id) => id !== option.type_id,
                              );
                              formik.setFieldValue("type", newValues);
                            }}
                          >
                            ✕
                          </span>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Types"
                      name="type"
                      variant="outlined"
                      size="small"
                      error={!!(formik.errors.type && formik.touched.type)}
                      helperText={
                        formik.touched.type
                          ? (formik.errors.type as string)
                          : ""
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(!formik.values.sub_category),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Box
                          key={option.category_id}
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            backgroundColor: MecordColors.ActiveBackground,
                            borderRadius: "3px",
                            padding: "2px 6px",
                            margin: "1px",
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "13px",
                          }}
                          {...tagProps}
                        >
                          {option.type_name}
                        </Box>
                      );
                    })
                  }
                  disabled={!formik.values.sub_category}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Make <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={makes}
                  loading={dropDownLoading}
                  getOptionLabel={(option: any) => option.make_name || ""}
                  value={
                    makes.find((m) => m.make_id === formik.values.make) || null
                  }
                  onChange={(event, newValue: any) => {
                    formik.setFieldValue("make", newValue?.make_id || "");
                    // formik.setFieldTouched("make", true);
                  }}
                  onBlur={() => formik.setFieldTouched("make", true)}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt.make_name,
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="make"
                      placeholder="Select Make"
                      variant="outlined"
                      size="small"
                      error={!!(formik.errors.make && formik.touched.make)}
                      helperText={
                        formik.touched.make
                          ? (formik.errors.make as string)
                          : ""
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="fw-semibold mb-0">
                    Model <span className="text-danger">*</span>
                  </Form.Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="p-0"
                    onClick={() => setShowQuickModelModal(true)}
                  >
                    + Add Model
                  </Button>
                </div>
                <Autocomplete
                  options={models}
                  getOptionLabel={(option: any) => option.model_name || ""}
                  value={
                    models.find((m) => m.model_id === formik.values.model) ||
                    null
                  }
                  onChange={(event, newValue: any) => {
                    formik.setFieldValue("model", newValue?.model_id || "");
                    // formik.setFieldTouched("model", true);
                  }}
                  onBlur={() => formik.setFieldTouched("model", true)}
                  disabled={!formik.values.make}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt.model_name,
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="model"
                      placeholder="Select Model"
                      variant="outlined"
                      size="small"
                      error={!!(formik.errors.model && formik.touched.model)}
                      helperText={
                        formik.touched.model
                          ? (formik.errors.model as string)
                          : ""
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(!formik.values.make),
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  SKU Code <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="product_sku"
                  placeholder="Enter SKU Code"
                  value={formik.values.product_sku}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!(formik.errors.product_sku && formik.touched.product_sku)
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.product_sku}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Unit <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={units}
                  loading={dropDownLoading}
                  getOptionLabel={(option: any) => option.unit_name || ""}
                  value={
                    units.find((u) => u.unit_id === formik.values.unit) || null
                  }
                  onChange={(event, newValue: any) => {
                    formik.setFieldValue("unit", newValue?.unit_id || "");
                    // formik.setFieldTouched("unit", true);
                  }}
                  onBlur={() => formik.setFieldTouched("unit", true)}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt.unit_name,
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="unit"
                      placeholder="Select Unit"
                      variant="outlined"
                      size="small"
                      error={!!(formik.errors.unit && formik.touched.unit)}
                      helperText={
                        formik.touched.unit
                          ? (formik.errors.unit as string)
                          : ""
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                      }}
                    />
                  )}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Unit Price
                </Form.Label>
                <Form.Control
                  name="unit_price"
                  type="number"
                  step="0.01"
                  placeholder="Enter Unit Price"
                  value={formik.values.unit_price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!(formik.errors.unit_price && formik.touched.unit_price)
                  }
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.unit_price}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <hr />
          {/* Additional Information */}
          <div className="mt-4">
            <h5 className="mb-3">Additional Information</h5>
            <Row className="mb-3">
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    HSN Code <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={hsnCodes}
                    loading={dropDownLoading}
                    getOptionLabel={(option: any) => option.hsn_code || ""}
                    value={
                      hsnCodes.find(
                        (h) => h.hsn_code_id === formik.values.hsn,
                      ) || null
                    }
                    onChange={(event, newValue: any) => {
                      formik.setFieldValue(
                        "hsn",
                        newValue?.hsn_code_id || "",
                        true,
                      );
                      // formik.setFieldTouched("hsn", true);
                      if (newValue?.gst?.gst_percentage) {
                        formik.setFieldValue(
                          "gst",
                          newValue?.gst?.gst_percentage.toString(),
                          false,
                        );
                      } else {
                        formik.setFieldValue("gst", "");
                      }
                    }}
                    onBlur={() => formik.setFieldTouched("hsn", true)}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt.hsn_code,
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="hsn"
                        placeholder="Select HSN Code"
                        variant="outlined"
                        size="small"
                        error={!!(formik.errors.hsn && formik.touched.hsn)}
                        helperText={
                          formik.touched.hsn
                            ? (formik.errors.hsn as string)
                            : ""
                        }
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                        }}
                      />
                    )}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    GST %{/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Form.Control
                    name="gst"
                    value={formik.values.gst}
                    placeholder="Autofill"
                    disabled
                    // isInvalid={!!(formik.errors.gst && formik.touched.gst)}
                    style={commonInputStyle}
                    className="form-control-placeholder"
                  />
                  {/* <Form.Control.Feedback type="invalid">
                    {formik.errors.gst}
                  </Form.Control.Feedback> */}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Description</Form.Label>
                  <TextEditor
                    value={formik.values.description || ""}
                    onChange={(html) =>
                      formik.setFieldValue("description", html)
                    }
                    placeholder="Enter product description (supports bold, italic, lists, etc.)"
                    minHeightPx={120}
                    maxHeightPx={240}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Upload Image - same UI as enquiry / quotation / sales order attach files */}
            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-1 gap-2 w-50">
                    <h5 className="fw-semibold mb-0">Upload Image</h5>
                  </div>
                  <div className="position-relative w-50">
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const target = e.target as HTMLInputElement;
                        const file = target.files?.[0] || null;
                        console.log("file", file);
                        setImage(file);
                        if (file) setExistingImageUrl(null);
                      }}
                      style={{ display: "none" }}
                      id="product-form-product-image"
                    />
                    <Form.Label
                      htmlFor="product-form-product-image"
                      className="w-100 d-flex align-items-center justify-content-between p-2 border rounded mb-0"
                      style={{ cursor: "pointer" }}
                    >
                      <span className="text-muted">
                        {image || existingImageUrl
                          ? "1 file attached"
                          : "Upload Image"}
                      </span>
                      <Upload size={18} />
                    </Form.Label>
                  </div>
                  {(image || existingImageUrl) && (
                    <div className="mt-2 w-50">
                      <div
                        className="d-flex align-items-center justify-content-between p-2 border rounded"
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        <a
                          href={
                            image
                              ? (imagePreviewUrlRef.current || "#")
                              : existingImageUrl || "#"
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-flex align-items-center gap-2 text-decoration-none"
                          style={{
                            fontSize: "0.875rem",
                            maxWidth: "calc(100% - 40px)",
                            color: existingImageUrl ? "#0d6efd" : "inherit",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            if (existingImageUrl && !image) {
                              e.preventDefault();
                              window.open(existingImageUrl, "_blank");
                            }
                          }}
                        >
                          {image && imagePreviewUrlRef.current && (
                            <img
                              src={imagePreviewUrlRef.current}
                              alt="preview"
                              style={{
                                width: 36,
                                height: 36,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                          )}
                 
                 <span
                          className="text-truncate"
                          title={
                            image
                              ? image.name
                              : existingImageUrl
                                ? existingImageUrl.split("/").pop()?.split("?")[0] || "Product image"
                                : "Product image"
                          }
                        >
                          {image
                            ? image.name
                            : existingImageUrl
                              ? existingImageUrl.split("/").pop()?.split("?")[0] || "Product image"
                              : "Product image"}
                        </span>
                        </a>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            setImage(null);
                            setExistingImageUrl(null);
                          }}
                          style={{
                            backgroundColor: "rgba(220, 53, 69, 0.9)",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>


          <style>
            {`
              .bright-white-close .btn-close {
                filter: invert(1) brightness(2);
                opacity: 1;
              }
            `}
          </style>
          <Modal
            show={showQuickTypeModal}
            onHide={() => !isQuickTypeSaving && setShowQuickTypeModal(false)}
            centered
          >
            <Modal.Header
              closeButton
              closeVariant="white"
              className="bright-white-close"
              style={{
                backgroundColor: MecordColors.ThemeBGColor,
                paddingBottom: "12px",
              }}
            >
              <Modal.Title style={{ color: "#fff" }}>Add Type</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Label className="fw-semibold">
                    Category <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={categories}
                    getOptionLabel={(option: any) => option.category_name || ""}
                    value={
                      categories.find(
                        (category: any) =>
                          category.category_id === formik.values.category
                      ) || null
                    }
                    onChange={(event, newValue: any) => {
                      formik.setFieldValue("category", newValue?.category_id || "");
                      formik.setFieldValue("sub_category", "");
                      formik.setFieldValue("type", []);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Category"
                        variant="outlined"
                        size="small"
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="fw-semibold">
                    Sub-Category <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={subCategories}
                    getOptionLabel={(option: any) =>
                      option.sub_category_name || ""
                    }
                    value={
                      subCategories.find(
                        (subCategory: any) =>
                          subCategory.sub_category_id ===
                          formik.values.sub_category
                      ) || null
                    }
                    onChange={(event, newValue: any) => {
                      formik.setFieldValue(
                        "sub_category",
                        newValue?.sub_category_id || ""
                      );
                      formik.setFieldValue("type", []);
                    }}
                    disabled={!formik.values.category}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Sub-Category"
                        variant="outlined"
                        size="small"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          ...getDisabledInputStyles(!formik.values.category),
                        }}
                      />
                    )}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label className="fw-semibold">
                    Type Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    value={quickTypeName}
                    onChange={(event) => setQuickTypeName(event.target.value)}
                    placeholder="Enter Type Name"
                    style={commonInputStyle}
                    autoFocus
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <CancelButton
                onClick={() => setShowQuickTypeModal(false)}
                disabled={isQuickTypeSaving}
                showConfirmation={false}
              />
              <PrimarySubmitButton
                type="button"
                label="Add Type"
                isLoading={isQuickTypeSaving}
                loadingLabel="Adding..."
                onClick={handleQuickAddType}
              />
            </Modal.Footer>
          </Modal>

          <Modal
            show={showQuickModelModal}
            onHide={() => !isQuickModelSaving && setShowQuickModelModal(false)}
            centered
          >
            <Modal.Header
              closeButton
              closeVariant="white"
              className="bright-white-close"
              style={{
                backgroundColor: MecordColors.ThemeBGColor,
                paddingBottom: "12px",
              }}
            >
              <Modal.Title style={{ color: "#fff" }}>Add Model</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Label className="fw-semibold">
                    Make <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={makes}
                    getOptionLabel={(option: any) => option.make_name || ""}
                    value={
                      makes.find(
                        (make: any) => make.make_id === formik.values.make
                      ) || null
                    }
                    onChange={(event, newValue: any) => {
                      formik.setFieldValue("make", newValue?.make_id || "");
                      formik.setFieldValue("model", "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Make"
                        variant="outlined"
                        size="small"
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label className="fw-semibold">
                    Model Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    value={quickModelName}
                    onChange={(event) => setQuickModelName(event.target.value)}
                    placeholder="Enter Model Name"
                    style={commonInputStyle}
                    autoFocus
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <CancelButton
                onClick={() => setShowQuickModelModal(false)}
                disabled={isQuickModelSaving}
                showConfirmation={false}
              />
              <PrimarySubmitButton
                type="button"
                label="Add Model"
                isLoading={isQuickModelSaving}
                loadingLabel="Adding..."
                onClick={handleQuickAddModel}
              />
            </Modal.Footer>
          </Modal>
          {/* Action Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-4">
            {isModal ? (
              <CancelButton onClick={onSuccess} disabled={isSubmitting} />
            ) : (
              <CancelButton
                redirectTo="/master/product"
                disabled={isSubmitting}
              />
            )}
            <PrimarySubmitButton
              label={isEdit ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={isEdit ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </>
  );

  if (isModal) {
    return formContent;
  }

  return <PageContainer>{formContent}</PageContainer>;
};

export default ProductForm;


